import React from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Box, CircularProgress, IconButton, Modal, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { StyledAgGridContainer } from "components/StyledComponents/styledComponents.js";

function formatDateToDDMonYYYY(dateString) {
  if (!dateString) return "";
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  return `${String(date.getDate()).padStart(2, "0")}-${
    months[date.getMonth()]
  }-${date.getFullYear()}`;
}

const MODE_CONFIG = {
  gstLiability: {
    amountField: "amount",
    amountHeader: "Amount (₹)",
    extraCol: { headerName: "Net GST Liability (₹)", field: "netGstLiability" },
    footerFields: ["amount", "netGstLiability"],
    extraCols: null,
  },
  igstLiability: {
    amountField: "amount",
    amountHeader: "Amount (₹)",
    extraCol: { headerName: "Net IGST Liability (₹)", field: "netGstLiability" },
    footerFields: ["amount", "netGstLiability"],
    extraCols: null,
  },
  gstPaid: {
    amountField: "gstPaidAmount",
    amountHeader: "GST Paid Amount (₹)",
    extraCol: null,
    footerFields: ["gstPaidAmount"],
    extraCols: null,
  },
  igstPaid: {
    amountField: "gstPaidAmount",
    amountHeader: "IGST Paid Amount (₹)",
    extraCol: null,
    footerFields: ["gstPaidAmount"],
    extraCols: null,
  },
  gstReceivable: {
    amountField: "gstReceivable",
    amountHeader: "GST Receivable (₹)",
    extraCol: null,
    footerFields: ["gstReceivable", "inputGSTAmount", "inputIGSTAmount", "totalGSTRecevable"],
    extraCols: [
      { headerName: "Input GST Amount (₹)", field: "inputGSTAmount" },
      { headerName: "Input IGST Amount (₹)", field: "inputIGSTAmount" },
      { headerName: "Total GST Receivable (₹)", field: "totalGSTRecevable" },
    ],
  },
};

const GstDialogPopup = ({ open, onClose, loading, data, mode }) => {
  const config = MODE_CONFIG[mode] || MODE_CONFIG.gstLiability;

  const computeFooter = (rows) => {
    const totals = {};
    config.footerFields.forEach((f) => {
      totals[f] = rows.reduce((s, r) => s + (r[f] || 0), 0);
    });
    return [{ tranDate: "", type: "", remarks: "TOTAL", ...totals, footerRow: true }];
  };

  const columnDefs = [
    {
      headerName: "Date",
      field: "tranDate",
      minWidth: 130,
      flex: 1,
      valueFormatter: (params) => (params.value ? formatDateToDDMonYYYY(params.value) : ""),
    },
    {
      headerName: "Type",
      field: "type",
      minWidth: 100,
      flex: 0.8,
      cellRenderer: (params) => {
        if (!params.value) return "";
        const isCR = params.value === "CR";
        return (
          <span
            style={{
              backgroundColor: isCR ? "#e6f0ff" : "#ffe6e6",
              color: isCR ? "#1a73e8" : "#d93025",
              padding: "3px 10px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      headerName: "Remarks",
      field: "remarks",
      minWidth: 250,
      flex: 2,
    },
    ...(mode !== "gstReceivable"
      ? [
          {
            headerName: config.amountHeader,
            field: config.amountField,
            minWidth: 160,
            flex: 1,
            cellStyle: (params) => ({
              textAlign: "right",
              fontWeight: "600",
              color: params.data?.footerRow
                ? "#D46B08"
                : (params.value ?? 0) < 0
                ? "#c62828"
                : "#2e7d32",
            }),
            valueFormatter: (params) =>
              params.value != null
                ? `₹ ${params.value.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                : "",
          },
        ]
      : []),
    ...(config.extraCol
      ? [
          {
            headerName: config.extraCol.headerName,
            field: config.extraCol.field,
            minWidth: 180,
            flex: 1,
            cellStyle: (params) => ({
              textAlign: "right",
              fontWeight: "600",
              color: params.data?.footerRow
                ? "#D46B08"
                : (params.value ?? 0) < 0
                ? "#c62828"
                : "#2e7d32",
            }),
            valueFormatter: (params) =>
              params.value != null
                ? `₹ ${params.value.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                : "",
          },
        ]
      : []),
    ...(config.extraCols
      ? config.extraCols.map((col) => ({
          headerName: col.headerName,
          field: col.field,
          minWidth: 180,
          flex: 1,
          cellStyle: (params) => ({
            textAlign: "right",
            fontWeight: "600",
            color: params.data?.footerRow
              ? "#D46B08"
              : (params.value ?? 0) < 0
              ? "#c62828"
              : "#2e7d32",
          }),
          valueFormatter: (params) =>
            params.value != null
              ? `₹ ${params.value.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
              : "",
        }))
      : []),
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        elevation={6}
        sx={{
          position: "absolute",
          top: "8vh",
          left: "5vw",
          width: "90vw",
          height: "84vh",
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 1, flexShrink: 0 }}>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: "hidden", px: 2, pb: 2 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress sx={{ color: "#D46B08" }} />
            </Box>
          ) : (
            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{ height: "100%", width: "100%" }}
            >
              <AgGridReact
                enableCellTextSelection
                enableRangeSelection
                suppressClipboardPaste={false}
                rowData={data}
                pagination
                paginationPageSize={50}
                defaultColDef={{ resizable: true, sortable: true }}
                animateRows
                columnDefs={columnDefs}
                pinnedBottomRowData={computeFooter(data)}
                getRowStyle={(params) => {
                  if (params.data?.footerRow)
                    return { fontWeight: "bold", backgroundColor: "#FFF3E0" };
                  return { backgroundColor: "#fff" };
                }}
              />
            </StyledAgGridContainer>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

GstDialogPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  mode: PropTypes.string.isRequired,
};

export default GstDialogPopup;
